/**
 * Failures, said in a way the person on the other side can act on.
 *
 * The API's own messages are written for developers ("Invalid credentials", "Invalid TOTP code"),
 * and the pages used to print them straight onto the screen — English text on a Darija login page
 * read by someone who may not read any Latin script at all. So the raw message is never shown:
 * each failure is classified and replaced with a plain sentence in the reader's own language.
 *
 * Only the wording is decided here. No auth rule, request, or condition changes: the same failures
 * happen at the same moments — they are just described in plain language.
 */

export interface ApiFailure extends Error {
  /** HTTP status, absent when the request never reached the server (offline, DNS, CORS). */
  status?: number
  /** Laravel's validation payload on a 422: { field: [messages] }. */
  errors?: Record<string, string[]>
}

/** Builds the error thrown by the auth client, keeping the status and validation payload. */
export function apiFailure(res: Response, body: unknown): ApiFailure {
  const payload = (body || {}) as { message?: string; errors?: Record<string, string[]> }
  const err = new Error(payload.message || `HTTP ${res.status}`) as ApiFailure
  err.status = res.status
  if (payload.errors && typeof payload.errors === 'object') err.errors = payload.errors
  return err
}

const FIELD_KEYS = ['email', 'password', 'phone', 'name'] as const

/**
 * @param t       the page's translation function
 * @param context 'totp' when a wrong code is the expected failure (a wrong code is a 422 with no
 *                validation payload, so it cannot be classified by field)
 */
export function plainError(e: unknown, t: (key: string) => string, context?: 'totp'): string {
  const err = (e || {}) as ApiFailure

  // No status at all: the request never got an answer, so this is a connection problem, not a
  // rejection by the server.
  if (!err.status) return t('auth.errors.offline')
  if (err.status === 401) return t('auth.errors.invalid_credentials')
  if (err.status === 429) return t('auth.errors.too_many')

  if (err.status === 422) {
    if (context === 'totp') return t('auth.errors.invalid_code')
    const field = Object.keys(err.errors || {})[0] as (typeof FIELD_KEYS)[number] | undefined
    if (field && (FIELD_KEYS as readonly string[]).includes(field)) return t(`auth.errors.${field}`)
    return t('auth.errors.check')
  }

  return t('auth.errors.generic')
}

export type FieldName = (typeof FIELD_KEYS)[number]

/**
 * What the server refused, field by field, as plain sentences under the right inputs. A 422 names
 * the fields it rejected; the page shows each one where it belongs instead of one vague sentence
 * above the form. Empty when the failure was not about a field (offline, rate limit, a wrong code).
 */
export function fieldErrors(e: unknown, t: (key: string) => string): Partial<Record<FieldName, string>> {
  const err = (e || {}) as ApiFailure
  const result: Partial<Record<FieldName, string>> = {}
  if (err.status !== 422 || !err.errors) return result

  for (const field of Object.keys(err.errors)) {
    if (!(FIELD_KEYS as readonly string[]).includes(field)) continue
    const key = field === 'email' ? 'email_taken' : field === 'password' ? 'password_short' : field
    result[field as FieldName] = t(`auth.errors.${key}`)
  }

  return result
}
